import { useMutation } from "@tanstack/react-query";

import {
  updateMainDealerCasinoRealMode,
  UpdateMainDealerCasinoRealModeParams,
  MainDealerCasinoRealModeResult,
} from "@/api/mainDealers";
import { useMainDealersManager } from "@/tanstackQuery/queries/mainDealers";

export interface UpdateMainDealerCasinoRealModeOptions {
  onSuccess?: (data: MainDealerCasinoRealModeResult) => void;
  onError?: (error: Error) => void;
}

// Optimistic per-row flip: the switch reflects the confirmed intent for the
// in-flight window, the server's flag value replaces it on success, and the
// snapshot restores on error — the cache is owned by the manager, never by
// the component. The response is a flag-only subset of the dealer row, so
// success patches the field rather than replacing the whole row.
export const useUpdateMainDealerCasinoRealMode = (
  options?: UpdateMainDealerCasinoRealModeOptions
) => {
  const mainDealersManager = useMainDealersManager();

  return useMutation({
    mutationFn: (params: UpdateMainDealerCasinoRealModeParams) =>
      updateMainDealerCasinoRealMode(params),

    onMutate: (params) => {
      const previous = mainDealersManager.get();
      mainDealersManager.patchRow(params.dealerId, {
        casinoRealEnabled: params.enabled,
      });
      return { previous };
    },

    onSuccess: (data) => {
      mainDealersManager.patchRow(data.id, {
        casinoRealEnabled: data.casinoRealEnabled,
      });
      options?.onSuccess?.(data);
    },

    onError: (error, _params, context) => {
      if (context?.previous) {
        mainDealersManager.replace(context.previous);
      }
      options?.onError?.(error);
    },
  });
};
